import React from 'react'
import { Modal } from '../components/common/Modal'

interface SecurityModalProps {
  isOpen: boolean
  onClose: () => void
}

export function SecurityModal({ isOpen, onClose }: SecurityModalProps): React.ReactElement {
  const securityFeatures = [
    {
      title: 'Encryption',
      icon: '🔐',
      description: 'All data is encrypted in transit using HTTPS and at rest using industry-standard encryption.',
    },
    {
      title: 'COPPA Compliance',
      icon: '⚖️',
      description: 'We comply with COPPA regulations for children under 13. Parents have full control over their child\'s data.',
    },
    {
      title: 'No Third-Party Sharing',
      icon: '🚫',
      description: 'We never sell or share personal data with third parties for marketing or any other purpose.',
    },
    {
      title: 'Password Protection',
      icon: '🔑',
      description: 'Parent accounts are protected with strong password requirements and optional two-factor authentication.',
    },
    {
      title: 'Content Moderation',
      icon: '👀',
      description: 'All user-generated content is reviewed by our safety team to prevent inappropriate material.',
    },
    {
      title: 'Secure Payments',
      icon: '💳',
      description: 'Payment processing is handled by PCI-DSS compliant payment providers. We never store credit card data.',
    },
  ]

  const bestPractices = [
    {
      practice: 'Strong Password',
      advice: 'Use a unique password with uppercase, lowercase, numbers, and symbols. Minimum 8 characters.',
    },
    {
      practice: 'Regular Updates',
      advice: 'Keep the BabyKingdom app updated to receive the latest security patches and features.',
    },
    {
      practice: 'Monitor Activity',
      advice: 'Regularly check the Dashboard to review your child\'s activity and flag any unusual behavior.',
    },
    {
      practice: 'Safe Profile Names',
      advice: 'Avoid using real full names, birthdates, or locations in child profile names for anonymity.',
    },
    {
      practice: 'Trusted Devices',
      advice: 'Only access BabyKingdom from trusted devices. Log out from public computers after use.',
    },
    {
      practice: 'Report Issues',
      advice: 'If you notice suspicious activity, report it immediately to our security team.',
    },
  ]

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Security & Safety" size="lg">
      <div className="space-y-8">
        <div className="text-center">
          <div className="text-6xl mb-3">🛡️</div>
          <h3 className="text-2xl font-black text-purple-700 mb-2">Your Security is Our Priority</h3>
          <p className="text-gray-700 font-bold">
            Learn about the security measures we use to protect your family
          </p>
        </div>

        {/* Security Features */}
        <div>
          <h4 className="text-2xl font-black text-purple-700 mb-4 pb-2 border-b-4 border-purple-200">
            🔒 Our Security Features
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {securityFeatures.map((feature, idx) => (
              <div key={idx} className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border-4 border-green-200">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">{feature.icon}</span>
                  <h5 className="font-black text-green-700">{feature.title}</h5>
                </div>
                <p className="text-gray-700 font-semibold text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Best Practices */}
        <div>
          <h4 className="text-2xl font-black text-purple-700 mb-4 pb-2 border-b-4 border-purple-200">
            ✅ Security Best Practices
          </h4>
          <div className="space-y-3">
            {bestPractices.map((item, idx) => (
              <div key={idx} className="p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl border-4 border-blue-200">
                <div className="flex gap-3 items-start">
                  <span className="text-2xl font-black text-blue-500 mt-1">→</span>
                  <div>
                    <h5 className="font-black text-blue-700 mb-1">{item.practice}</h5>
                    <p className="text-gray-700 font-semibold text-sm">{item.advice}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reporting */}
        <div className="p-6 bg-gradient-to-r from-red-50 to-pink-50 rounded-2xl border-4 border-red-200">
          <h4 className="font-black text-red-700 mb-3 text-lg">⚠️ Found a Security Issue?</h4>
          <p className="text-gray-700 font-bold mb-4">
            We take security seriously. If you discover a vulnerability, please report it responsibly to our security team.
          </p>
          <div className="text-center">
            <a href="mailto:security@babykingdom.com" className="inline-block px-6 py-2 bg-red-500 text-white font-black rounded-full hover:bg-red-600">
              Report Security Issue
            </a>
          </div>
        </div>

        {/* Certifications */}
        <div className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl border-4 border-yellow-200">
          <h4 className="font-black text-yellow-700 mb-4">📜 Certifications & Compliance</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl mb-2">🏆</p>
              <p className="font-black text-gray-700 text-sm">COPPA<br />Compliant</p>
            </div>
            <div>
              <p className="text-2xl mb-2">🔐</p>
              <p className="font-black text-gray-700 text-sm">ISO 27001<br />Certified</p>
            </div>
            <div>
              <p className="text-2xl mb-2">💳</p>
              <p className="font-black text-gray-700 text-sm">PCI DSS<br />Level 1</p>
            </div>
            <div>
              <p className="text-2xl mb-2">🌍</p>
              <p className="font-black text-gray-700 text-sm">GDPR<br />Compliant</p>
            </div>
          </div>
        </div>

        <div className="text-center p-4 bg-purple-50 rounded-2xl border-4 border-purple-200">
          <p className="text-gray-700 font-bold">
            For detailed security information, see our Privacy Policy or contact security@babykingdom.com
          </p>
        </div>
      </div>
    </Modal>
  )
}
